import { set } from 'lodash';
import { isObjectType } from '../isObjectType';

export type RecursiveNullAsUndefined<T> = T extends null
  ? undefined
  : T extends [infer F, ...infer R]
    ? [RecursiveNullAsUndefined<F>, ...RecursiveNullAsUndefined<R>]
    : T extends []
      ? []
      : T extends Array<infer U>
        ? Array<RecursiveNullAsUndefined<U>>
        : T extends object
          ? { [K in keyof T]: RecursiveNullAsUndefined<T[K]> }
          : T;

/**
 * Removes all the `null` values in an object recursively.
 * Useful for working with GraphQL results where `undefined` values are always set as `null`.
 */
export const removeNullValues = <T extends object = Record<string, unknown>>(
  obj: T
): RecursiveNullAsUndefined<T> => {
  if (!isObjectType(obj)) {
    return (obj === null ? undefined : obj) as unknown as RecursiveNullAsUndefined<T>;
  }

  /**
   * @note Create a new instance that inherits the prototype
   * of the original object so that it works for classes as well.
   */
  const newObj = Object.create(Object.getPrototypeOf(obj)) as RecursiveNullAsUndefined<T>;

  for (const [key, val] of Object.entries(obj)) {
    if (isObjectType(val)) {
      set(newObj, key, removeNullValues(val));
    } else if (Array.isArray(val)) {
      set(
        newObj,
        key,
        val.map((element) => {
          if (isObjectType(element)) {
            return removeNullValues(element);
          }
          // For primitives or null within arrays
          return element === null ? undefined : element;
        })
      );
    } else if (val === null) {
      set(newObj, key, undefined);
    } else {
      set(newObj, key, val);
    }
  }
  return newObj;
};
